import { INVENTORY_MODULE } from "@/api_framework/api_config";
import { IDefaultTransferListItem, IDefaultTransferListResponse } from "@/api_framework/api_modals/Inventory";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

AxiosCurlirize(axios, () => {});

const CACHE_TTL_MS = 5 * 60 * 1000;

/* Module-level cache so repeated visits within the TTL skip the network call */
const mostSoldCache = new Map<string, { data: IDefaultTransferListItem[]; fetchedAt: number }>();

const getCacheKey = (clinicId: string, category: string) => `${clinicId}:${category}`;

/* Fetches "most sold" suggestions for several categories in parallel, keyed by category, cached for 5 minutes */
export const useMostSoldByCategoryAPI = () => {
  const configuration = useConfiguration();
  const [sectionsByCategory, setSectionsByCategory] = useState<Record<string, IDefaultTransferListItem[]>>({});
  const [loadingByCategory, setLoadingByCategory] = useState<Record<string, boolean>>({});
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();

  const fetchCategory = useCallback(async (clinicId: string, category: string) => {
    const cacheKey = getCacheKey(clinicId, category);
    const cached = mostSoldCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setSectionsByCategory((prev) => ({ ...prev, [category]: cached.data }));
      return;
    }

    setLoadingByCategory((prev) => ({ ...prev, [category]: true }));
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const response = await axios
        .get(
          `${INVENTORY_MODULE?.DEFAULT_TRANSFER_LIST?.baseURL}?category=${category}&clinic_id=${clinicId}&page_size=10&page_number=1`,
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        )
        .then((res: AxiosResponse<IDefaultTransferListResponse>) => (res.data.status ? res.data.data.data ?? [] : []))
        .catch((e: AxiosError) => {
          sendCurl(e.config["curlCommand"]);
          captureError(e);
          return [];
        });
      mostSoldCache.set(cacheKey, { data: response, fetchedAt: Date.now() });
      setSectionsByCategory((prev) => ({ ...prev, [category]: response }));
    } catch (e: any) {
      toast.error("Server Error: " + e.message);
      setSectionsByCategory((prev) => ({ ...prev, [category]: [] }));
    } finally {
      setLoadingByCategory((prev) => ({ ...prev, [category]: false }));
    }
  }, []);

  const fetchAll = useCallback(
    (clinicId: string, categories: string[]) => {
      categories.forEach((category) => fetchCategory(clinicId, category));
    },
    [fetchCategory],
  );

  return { sectionsByCategory, loadingByCategory, fetchAll };
};
